// CHG-123 REQ-V123-021: 메시지 → 작업/할 일 1단계 선택 모달
import { useEffect } from 'react';
import { X, Briefcase, CheckSquare } from 'lucide-react';
import toast from 'react-hot-toast';
import type { ChatMessage } from '../../services/chatService';

interface MessageToTaskModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  message: ChatMessage;
  contextProjectId?: string;
  contextProjectKey?: string;
  /** "작업 생성하기" 버튼 클릭 — Step 4에서 연결 */
  onCreateIssue?: () => void;
  /** "할 일 생성하기" 버튼 클릭 — Step 5에서 연결 */
  onCreateTodo?: () => void;
}

export default function MessageToTaskModal({
  open,
  onOpenChange,
  message,
  contextProjectId,
  contextProjectKey,
  onCreateIssue,
  onCreateTodo,
}: MessageToTaskModalProps) {
  // ESC로 닫기
  useEffect(() => {
    if (!open) return;
    const handle = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onOpenChange(false);
    };
    document.addEventListener('keydown', handle);
    return () => document.removeEventListener('keydown', handle);
  }, [open, onOpenChange]);

  if (!open) return null;

  // 콜백이 있으면 부모(Flow)가 stage 전환 → 모달은 unmount되어 자동 닫힘
  // 없으면 (단독 사용) toast로 안내만
  const handleCreateIssue = () => {
    if (onCreateIssue) {
      onCreateIssue();
    } else {
      toast('다음 단계 미구현 — Step 4에서 연결됩니다');
    }
  };

  const handleCreateTodo = () => {
    if (onCreateTodo) {
      onCreateTodo();
    } else {
      toast('다음 단계 미구현 — Step 5에서 연결됩니다');
    }
  };

  // 1~2줄로 잘린 미리보기 (첫 두 줄)
  const previewLines = (message.content ?? '').split(/\r?\n/).slice(0, 2);
  const preview = previewLines.join(' ');
  const truncated = preview.length > 140 ? preview.slice(0, 137) + '…' : preview;

  // contextProjectKey/Id 표시는 단순 디버깅용 — Step 4에서 본격 사용
  void contextProjectId; void contextProjectKey;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="msg-to-task-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={() => onOpenChange(false)}
    >
      <div
        className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6"
        onClick={(e) => e.stopPropagation()}
      >
        {/* 헤더 */}
        <div className="flex items-center justify-between mb-4">
          <h3 id="msg-to-task-title" className="text-lg font-semibold text-gray-900">
            작업/할 일로 만들기
          </h3>
          <button
            onClick={() => onOpenChange(false)}
            className="p-1 text-gray-400 hover:text-gray-600 rounded"
            aria-label="닫기"
          >
            <X className="size-5" />
          </button>
        </div>

        {/* 두 주 버튼 */}
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={handleCreateIssue}
            className="flex-1 flex flex-col items-center gap-2 px-4 py-5 border border-gray-200 rounded-lg hover:bg-primary-50 hover:border-primary-300 transition-colors text-left"
          >
            <Briefcase className="size-7 text-primary-600" />
            <span className="text-sm font-semibold text-gray-900">작업 생성하기</span>
            <span className="text-sm text-gray-500 text-center">프로젝트의 작업으로 등록합니다</span>
          </button>
          <button
            onClick={handleCreateTodo}
            className="flex-1 flex flex-col items-center gap-2 px-4 py-5 border border-gray-200 rounded-lg hover:bg-primary-50 hover:border-primary-300 transition-colors text-left"
          >
            <CheckSquare className="size-7 text-primary-600" />
            <span className="text-sm font-semibold text-gray-900">할 일 생성하기</span>
            <span className="text-sm text-gray-500 text-center">내 마이 홈 할 일로 등록합니다</span>
          </button>
        </div>

        {/* 메시지 미리보기 */}
        {truncated && (
          <div className="mt-4 px-3 py-2 bg-gray-50 border border-gray-100 rounded-md">
            <p className="text-sm text-gray-400 mb-1">변환할 메시지</p>
            <p className="text-sm text-gray-700 line-clamp-2 whitespace-pre-wrap">{truncated}</p>
          </div>
        )}
      </div>
    </div>
  );
}
